import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import WeekScheduleSummary from './WeekScheduleSummary';
import UserDropdown from '../UserDropdown';
import EarningsBanner from './EarningsBanner';
import ShareBookingLinkModal from './ShareBookingLinkModal';
import CancelRideModal from '../Shared/CancelRideModal';
import ContactModal from '../common/ContactModal';
import ConfirmModal from '../common/ConfirmModal';
import DashboardTutorial from './DashboardTutorial';
import InviteStepModal from './InviteStepModal';
import AppDownloadModal from '../common/AppDownloadModal';
import { isNativeApp } from '../../utils/platform';
import { formatDateTime, ensureUTC } from '../../utils/dateUtils';

// Helper function to create Google Maps route URL
const createRouteURL = (pickupLat, pickupLng, dropoffLat, dropoffLng) => {
  const params = new URLSearchParams({
    api: '1',
    destination: `${dropoffLat},${dropoffLng}`,
    waypoints: `${pickupLat},${pickupLng}`,
    travelmode: 'driving'
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

// Active Ride Card Component
const ActiveRideCard = ({ ride, onComplete, onDidNotOccur, onCancel, commissionRate = 0.30 }) => {
  const [loading, setLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showDidNotOccurConfirm, setShowDidNotOccurConfirm] = useState(false);

  const getRideStatus = () => {
    const now = new Date();
    const startTime = new Date(ensureUTC(ride.pickup_datetime));
    const fifteenMinBefore = new Date(startTime.getTime() - 15 * 60 * 1000);

    if (now < fifteenMinBefore) {
      return { label: 'Upcoming', color: 'bg-gray-100 border-gray-300', textColor: 'text-gray-700' };
    } else if (now < startTime) {
      return { label: 'Starting Soon', color: 'bg-yellow-50 border-yellow-400', textColor: 'text-yellow-800' };
    } else {
      // Overdue = time since pickup was supposed to happen
      const minutesOverdue = Math.floor((now - startTime) / 1000 / 60);
      return {
        label: `${minutesOverdue} min overdue`,
        color: 'bg-red-50 border-red-400',
        textColor: 'text-red-800'
      };
    }
  };

  const formatTime = (datetime) => {
    return new Date(ensureUTC(datetime)).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (datetime) => {
    return new Date(ensureUTC(datetime)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleMarkComplete = async () => {
    setLoading(true);
    try {
      await onComplete(ride);
    } catch (error) {
      console.error('Error completing ride:', error);
      alert('Failed to complete ride. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDidNotOccur = async () => {
    setLoading(true);
    try {
      await onDidNotOccur(ride.id);
    } catch (error) {
      console.error('Error marking ride as did not occur:', error);
      alert('Failed to process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const status = getRideStatus();
  const totalFare = ride.total_fare || ride.final_price || 0;
  const driverEarnings = totalFare ? (totalFare * (1 - commissionRate)).toFixed(2) : '0.00';

  // Check if ride has started (for enabling Mark Complete button)
  const now = new Date();
  const startTime = new Date(ensureUTC(ride.pickup_datetime));
  const canMarkComplete = now >= startTime;

  return (
    <div className={`rounded-lg border-2 ${status.color} p-4 mb-3 shadow-sm transition-all hover:shadow-md`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold px-2 py-1 rounded ${status.color} ${status.textColor}`}>
              {status.label}
            </span>
            <span className="text-sm font-medium text-gray-600">
              {formatDate(ride.pickup_datetime)}
            </span>
          </div>
          <h3 className="text-lg font-bold text-gray-900">
            {ride.rider_name || 'Rider'}
          </h3>
        </div>
        <div className="text-right">
          {ride.is_day_booking_leg ? (
            <>
              <div className="text-lg font-bold text-orange-600">
                Leg {ride.sequence_number || 1} of {ride.max_sequence || '?'}
              </div>
              <div className="text-xs text-orange-500">
                {ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} Booking
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-green-600">
                ${totalFare.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500">
                You earn: ${driverEarnings}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth="2"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {formatTime(ride.pickup_datetime)}
              {ride.dropoff_datetime && ` - ${formatTime(ride.dropoff_datetime)}`}
            </p>
            <p className="text-xs text-gray-500">
              {ride.duration_minutes ? `${ride.duration_minutes} minutes` : ''}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              <span className="font-medium">From:</span> {ride.pickup_address || ride.pickup_location}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">To:</span> {ride.dropoff_address || ride.dropoff_location}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {ride.distance_miles} miles
            </p>
          </div>
        </div>
      </div>

      {/* Map Route Button */}
      {ride.pickup_lat && ride.pickup_lng && ride.dropoff_lat && ride.dropoff_lng && (
        <div className="mb-4">
          <a
            href={createRouteURL(ride.pickup_lat, ride.pickup_lng, ride.dropoff_lat, ride.dropoff_lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Map Route
          </a>
        </div>
      )}

      {/* Contact Rider Button */}
      <div className="mb-4">
        <button
          onClick={() => setShowContactModal(true)}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Rider
        </button>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setShowCompleteConfirm(true)}
          disabled={loading || !canMarkComplete}
          title={!canMarkComplete ? 'You can mark the ride complete after the scheduled start time' : 'Mark this ride as complete'}
          className={`flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-colors ${
            loading || !canMarkComplete
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {loading ? 'Processing...' : canMarkComplete ? 'Mark Complete' : 'Not Started Yet'}
        </button>

        <button
          onClick={() => setShowDidNotOccurConfirm(true)}
          disabled={loading}
          className={`px-4 py-3 rounded-lg font-semibold transition-colors border-2 ${
            loading
              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
              : 'bg-white text-red-600 border-red-300 hover:bg-red-50'
          }`}
        >
          Did Not Occur
        </button>

        {/* Only show cancel button for regular rides, not day booking legs */}
        {!ride.is_day_booking_leg && (
          <button
            onClick={() => setShowCancelModal(true)}
            disabled={loading}
            className={`px-4 py-3 rounded-lg font-semibold transition-colors border-2 ${
              loading
                ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                : 'bg-white text-orange-600 border-orange-300 hover:bg-orange-50'
            }`}
            title="Cancel ride"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Only render modal for regular rides, not day booking legs */}
      {!ride.is_day_booking_leg && (
        <CancelRideModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          ride={ride}
          userType="driver"
          onCancellationSuccess={() => {
            setShowCancelModal(false);
            onCancel && onCancel();
          }}
        />
      )}

      {/* Contact Rider Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        contactName={ride.rider_name}
        contactPhone={ride.rider_phone}
        contactType="rider"
      />

      <ConfirmModal
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={handleMarkComplete}
        title="Mark Ride Complete"
        message="Are you sure you want to mark this ride as complete?"
        confirmText="Mark Complete"
        confirmColor="green"
      />

      <ConfirmModal
        isOpen={showDidNotOccurConfirm}
        onClose={() => setShowDidNotOccurConfirm(false)}
        onConfirm={handleDidNotOccur}
        title="Ride Did Not Occur"
        message="Mark this ride as &quot;Did Not Occur&quot;? This will cancel the ride."
        confirmText="Did Not Occur"
        confirmColor="red"
      />
    </div>
  );
};

// Upcoming Ride Card Component (for scheduled rides not yet active)
const UpcomingRideCard = ({ ride, onCancel, onComplete }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  const formatTime = (datetime) => {
    return new Date(ensureUTC(datetime)).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Check if the ride has started (can be marked complete)
  const now = new Date();
  const startTime = new Date(ensureUTC(ride.pickup_datetime));
  const canMarkComplete = now >= startTime;

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await onComplete(ride);
    } catch (error) {
      console.error('Error completing ride:', error);
      alert('Failed to complete ride. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  // Determine fare display
  const getFareDisplay = () => {
    if (ride.is_day_booking_leg) {
      // For day booking legs, show rate type info instead of $0.00
      const rateLabel = ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day';
      // sequence_number starts at 1, we don't have total legs directly but can infer from is_final_leg
      return `${rateLabel} Booking • Leg ${ride.sequence_number || 1}`;
    }
    return `$${(ride.total_fare || ride.final_price || 0).toFixed(2)} • ${ride.distance_miles ? ride.distance_miles.toFixed(1) : '0'} miles`;
  };

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50 relative">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {/* Day booking badge */}
          {ride.is_day_booking_leg && (
            <span className="inline-block mb-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-medium">
              {ride.is_final_leg ? 'Final Leg' : 'Day Booking'}
            </span>
          )}
          <div className="font-semibold">
            {ride.pickup_address || ride.pickup_location} → {ride.dropoff_address || ride.dropoff_location}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {formatTime(ride.pickup_datetime)}
          </div>
          <div className="text-sm text-gray-600">
            {getFareDisplay()}
          </div>
          {ride.rider_name && (
            <div className="text-sm text-gray-500 mt-1">
              Rider: {ride.rider_name}
            </div>
          )}
        </div>
        {/* Only show cancel button for regular rides, not day booking legs */}
        {!ride.is_day_booking_leg && (
          <button
            onClick={() => setShowCancelModal(true)}
            className="ml-2 p-2 rounded-full hover:bg-red-50 text-red-600 transition-colors"
            title="Cancel ride"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Map Route Button */}
      {ride.pickup_lat && ride.pickup_lng && ride.dropoff_lat && ride.dropoff_lng && (
        <div className="mt-3">
          <a
            href={createRouteURL(ride.pickup_lat, ride.pickup_lng, ride.dropoff_lat, ride.dropoff_lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Map Route
          </a>
        </div>
      )}

      {/* Contact Rider Button */}
      <div className="mt-3">
        <button
          onClick={() => setShowContactModal(true)}
          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Rider
        </button>
      </div>

      {/* Complete Button */}
      <div className="mt-3">
        <button
          onClick={() => setShowCompleteConfirm(true)}
          disabled={completing || !canMarkComplete}
          title={!canMarkComplete ? 'You can mark the ride complete after the scheduled start time' : 'Mark this ride as complete'}
          className={`w-full px-4 py-2 rounded-lg font-semibold text-white transition-colors text-sm ${
            completing || !canMarkComplete
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {completing ? 'Completing...' : canMarkComplete ? 'Mark Complete' : 'Not Started Yet'}
        </button>
      </div>

      {/* Only render modal for regular rides, not day booking legs */}
      {!ride.is_day_booking_leg && (
        <CancelRideModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          ride={ride}
          userType="driver"
          onCancellationSuccess={() => {
            setShowCancelModal(false);
            onCancel && onCancel();
          }}
        />
      )}

      {/* Contact Rider Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        contactName={ride.rider_name}
        contactPhone={ride.rider_phone}
        contactType="rider"
      />

      <ConfirmModal
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={handleComplete}
        title="Mark Ride Complete"
        message="Are you sure you want to mark this ride as complete?"
        confirmText="Mark Complete"
        confirmColor="green"
      />
    </div>
  );
};

// Driver Day Booking Cancel Modal Component
const DriverDayBookingCancelModal = ({ booking, onClose, onCancel }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onCancel(booking.id, reason);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime) => {
    const timeStr = datetime.endsWith('Z') ? datetime : datetime + 'Z';
    return new Date(timeStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Cancel Day Booking?</h2>
              <p className="text-sm text-gray-600">
                {booking.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} Booking
              </p>
            </div>
          </div>

          {/* Booking Details */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Rider:</strong> {booking.rider_name || 'N/A'}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Date:</strong> {new Date(ensureUTC(booking.start_time)).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                
              })}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Time:</strong> {formatTime(booking.start_time)} - {formatTime(booking.end_time)} CT
            </p>
            <p className="text-sm text-gray-600">
              <strong>Your Payout</strong> <span className="font-normal">(net of tax)</span><strong>:</strong> ${parseFloat(booking.driver_payout || 0).toFixed(2)}
            </p>
          </div>

          {/* Warning */}
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> When you cancel a scheduled day booking, the rider's payment method will not be charged. Please only cancel if absolutely necessary.
            </p>
          </div>

          {/* Reason Input */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Reason for cancellation (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              rows="3"
              placeholder="Let the rider know why you're cancelling..."
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Keep Booking
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Cancelling...' : 'Confirm Cancellation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DriverDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [rideRequests, setRideRequests] = useState([]);
  const [dayBookingRequests, setDayBookingRequests] = useState([]);
  const [scheduledDayBookings, setScheduledDayBookings] = useState([]);
  const [upcomingRides, setUpcomingRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recentRides, setRecentRides] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [dayBookingCancelModal, setDayBookingCancelModal] = useState({ isOpen: false, booking: null });
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasUnpricedSlots, setHasUnpricedSlots] = useState(false);
  const [hasAvailabilityIssue, setHasAvailabilityIssue] = useState(false);
  const [hasPricingIssue, setHasPricingIssue] = useState(false);
  const [accountStatus, setAccountStatus] = useState({ is_active: true });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAppDownload, setShowAppDownload] = useState(false);

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 1 minute
    const interval = setInterval(() => {
      fetchDashboardData(true); // Pass true for silent refresh
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Check for tutorial query param on mount
  useEffect(() => {
    const checkTutorialStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const shouldShowTutorial = urlParams.get('showTutorial') === 'true';

      // Clean up URL immediately
      if (shouldShowTutorial) {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }

      // Check localStorage first (fast check)
      const localCompleted = localStorage.getItem('driver_tutorial_completed');
      if (localCompleted === 'true') {
        console.log('Tutorial already completed (localStorage)');
        return;
      }

      // Check database for tutorial status
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/drivers/onboarding-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const dbCompleted = response.data.tutorial_completed;
        console.log('Tutorial check:', { shouldShowTutorial, localCompleted, dbCompleted });

        if (dbCompleted) {
          // Sync localStorage with database
          localStorage.setItem('driver_tutorial_completed', 'true');
          console.log('Tutorial already completed (database)');

          // Check if invite step still needs to be shown
          const inviteStepSeen = response.data.invite_step_seen;
          if (!inviteStepSeen) {
            console.log('Showing standalone invite step modal...');
            setShowInviteModal(true);
          } else if (!isNativeApp()) {
            // Tutorial done, invite seen — check app download
            const appDismissed = response.data.app_download_dismissed;
            if (!appDismissed) {
              console.log('Showing app download modal...');
              setShowAppDownload(true);
            }
          }
          return;
        }

        // Show tutorial if query param is present and not completed
        if (shouldShowTutorial && !dbCompleted) {
          console.log('Showing tutorial...');
          setShowTutorial(true);
        }
      } catch (error) {
        console.error('Error checking tutorial status:', error);
        // Fallback to localStorage-only behavior if API fails
        if (shouldShowTutorial && !localCompleted) {
          setShowTutorial(true);
        }
      }
    };

    checkTutorialStatus();
  }, []); // Run only once on mount

  const fetchDashboardData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);

      const token = localStorage.getItem('token');

      // Fetch from the correct endpoints
      // Use /rides/recent for recent rides - it aggregates day bookings by booking_id
      const [profileRes, upcomingRidesRes, rideRequestsRes, recentRidesRes, dayBookingRes, scheduledDayBookingsRes, unpricedRes, onboardingStatusRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/profile`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/dashboard/upcoming-rides`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/ride-requests/pending`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/rides/recent`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/pending-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/bookings`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching scheduled day bookings:', err);
          return { data: { bookings: [] } };
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/unpriced-availability`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error checking unpriced availability:', err);
          return { data: { has_unpriced_slots: false } };
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error checking onboarding status:', err);
          return { data: { has_availability_issue: false, has_pricing_issue: false } };
        })
      ]);

      setProfile(profileRes.data);
      setUpcomingRides(upcomingRidesRes.data || []);
      setRideRequests(rideRequestsRes.data || []);
      setRecentRides(recentRidesRes.data || []);
      setDayBookingRequests(dayBookingRes.data.requests || []);
      // Filter to only show scheduled day bookings (not completed or cancelled)
      const activeBookings = (scheduledDayBookingsRes.data.bookings || []).filter(
        b => b.status === 'scheduled' || b.status === 'in_progress'
      );
      setScheduledDayBookings(activeBookings);
      setHasUnpricedSlots(unpricedRes.data?.has_unpriced_slots || false);
      setHasAvailabilityIssue(onboardingStatusRes.data?.has_availability_issue || false);
      setHasPricingIssue(onboardingStatusRes.data?.has_pricing_issue || false);

      // Fetch account activation status
      try {
        const statusRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/account-status`);
        setAccountStatus(statusRes.data);
      } catch (err) {
        console.error('Error fetching account status:', err);
      }

      // Debug: Log what we received
      console.log('[DRIVER DASHBOARD] Fetched upcoming rides:', upcomingRidesRes.data);
      console.log('[DRIVER DASHBOARD] Number of rides:', upcomingRidesRes.data?.length || 0);
      console.log('[DRIVER DASHBOARD] Fetched ride requests:', rideRequestsRes.data);
      console.log('[DRIVER DASHBOARD] Number of requests:', rideRequestsRes.data?.length || 0);
      console.log('[DRIVER DASHBOARD] Fetched day booking requests:', dayBookingRes.data.requests);
      console.log('[DRIVER DASHBOARD] Number of day bookings:', dayBookingRes.data.requests?.length || 0);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setUpcomingRides([]);
      setRideRequests([]);
      setRecentRides([]);
      setDayBookingRequests([]);
      setScheduledDayBookings([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handler to cancel a scheduled day booking
  const handleCancelDayBooking = async (bookingId, reason) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/driver/${bookingId}/cancel`,
        { reason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Day booking cancelled. The rider\'s payment method will not be charged.');
      setDayBookingCancelModal({ isOpen: false, booking: null });
      fetchDashboardData();
    } catch (error) {
      console.error('Error cancelling day booking:', error);
      window.alert(error.response?.data?.detail || 'Failed to cancel day booking');
    }
  };

  const handleCompleteRide = async (rideId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/${rideId}/complete`,
        {
          completed_by: 'driver',
          tip_amount: 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Ride marked as complete!');
      fetchDashboardData(true); // Refresh data
    } catch (error) {
      console.error('Error completing ride:', error);
      throw error;
    }
  };

  // Handler for completing any ride (regular or day booking leg)
  const handleCompleteUpcomingRide = async (ride) => {
    const token = localStorage.getItem('token');

    try {
      if (ride.is_day_booking_leg) {
        // Complete day booking leg - use leg_id for API call
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/day-bookings/legs/${ride.leg_id}/complete`,
          { completed_by: 'driver' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert(ride.is_final_leg ? 'Final leg completed! Booking marked as complete.' : 'Leg completed!');
      } else {
        // Complete regular ride
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/rides/${ride.id}/complete`,
          {
            completed_by: 'driver',
            tip_amount: 0
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Ride marked as complete!');
      }
      fetchDashboardData(true); // Refresh data
    } catch (error) {
      console.error('Error completing ride:', error);
      throw error;
    }
  };

  const handleDidNotOccur = async (rideId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/${rideId}/did-not-occur`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Ride marked as "Did Not Occur"');
      fetchDashboardData(true); // Refresh data
    } catch (error) {
      console.error('Error marking ride as did not occur:', error);
      throw error;
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData(true);
  };

  const handleTutorialComplete = async () => {
    // Set localStorage immediately for fast UX
    localStorage.setItem('driver_tutorial_completed', 'true');
    setShowTutorial(false);

    // Also save to database for cross-device/session persistence
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/tutorial-completed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Tutorial completion saved to database');
    } catch (error) {
      console.error('Failed to save tutorial completion to database:', error);
    }
  };

  const handleTutorialSkip = async () => {
    // Set localStorage immediately for fast UX
    localStorage.setItem('driver_tutorial_completed', 'true');
    setShowTutorial(false);

    // Also save to database for cross-device/session persistence
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/tutorial-completed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Tutorial skip saved to database');
    } catch (error) {
      console.error('Failed to save tutorial skip to database:', error);
    }

    // Driver skipped walkthrough — show standalone invite step modal
    setShowInviteModal(true);
  };

  const handleInviteStepDismiss = async () => {
    // Mark invite step as seen in localStorage for fast UX
    localStorage.setItem('driver_invite_step_seen', 'true');

    // Save to database
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/invite-step-seen`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Invite step seen saved to database');
    } catch (error) {
      console.error('Failed to save invite step seen to database:', error);
    }

    // Show app download modal next (if on web)
    if (!isNativeApp()) {
      setShowAppDownload(true);
    }
  };

  const handleAppDownloadDismiss = async () => {
    setShowAppDownload(false);

    // Fire-and-forget API call
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/app-download-dismissed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to save app download dismissal:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  // Get today's active rides (in_progress status only - rides that are currently happening)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayActiveRides = upcomingRides.filter(ride => {
    const now = new Date();

    // Only show rides where driver_status is 'pending' (scheduled) or 'active' (in progress)
    // Also include day booking statuses: 'scheduled' and 'in_progress'
    // This makes the active rides independent of rider status
    const validStatuses = ['pending', 'active', 'scheduled', 'in_progress'];
    if (!validStatuses.includes(ride.driver_status)) {
      return false;
    }

    // If ride is already active (in progress), always show it regardless of time
    if (ride.driver_status === 'active' || ride.driver_status === 'in_progress') {
      return true;
    }

    // For pending rides, only show within the time window
    const startTime = new Date(ensureUTC(ride.pickup_datetime));
    const endTime = ride.estimated_dropoff_datetime
      ? new Date(ride.estimated_dropoff_datetime)
      : new Date(startTime.getTime() + (ride.duration_minutes || 60) * 60000);

    // Show ride 15 minutes before pickup time
    const fifteenMinBefore = new Date(startTime.getTime() - 15 * 60 * 1000);
    const twoHoursAfterEnd = new Date(endTime.getTime() + 2 * 60 * 60 * 1000);

    // Show if ride is within 15 min before start OR within 2 hours of end time
    return now >= fifteenMinBefore && now <= twoHoursAfterEnd;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow sticky top-0 z-10 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Driver Dashboard</h1>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className={`p-2 rounded-lg transition-colors ${
                  refreshing 
                    ? 'bg-gray-100 text-gray-400 cursor-wait' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title="Refresh"
              >
                <svg 
                  className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <UserDropdown userType="driver" />
            </div>
          </div>
        </div>
      </header>

      {/* Deactivation Banner */}
      {!accountStatus.is_active && (
        <div className="bg-red-600 text-white px-4 py-3 text-center">
          <p className="font-bold text-sm sm:text-base">
            ACCOUNT DEACTIVATED — To request account reactivation, please email{' '}
            <a href="mailto:support@ridehowl.com?subject=Reactivate Account" className="underline">
              support@ridehowl.com
            </a>
            {' '}with Subject line "Reactivate Account" to apply for reactivation.
          </p>
        </div>
      )}

      {/* Earnings Banner - Sticky at top */}
      <EarningsBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        

        {/* Driver Profile Card */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              {profile?.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt={`${profile.first_name} ${profile.last_name}`}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-500 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold text-white flex-shrink-0">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </div>
              )}
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {profile?.first_name} {profile?.last_name}
                </h2>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{profile?.total_rides || 0} rides</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Link to="/driver/profile" data-tour="settings" className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-center text-sm sm:text-base">
                Edit Profile
              </Link>
              <button
                onClick={() => setShowShareModal(true)}
                data-tour="share-link"
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base"
              >
                <span>🔗</span>
                Invite Riders to Book
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Link to="/driver/ride-requests" data-tour="pending-requests" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-3xl font-bold text-blue-500 mb-2">{rideRequests.length + dayBookingRequests.length}</div>
            <div className="text-gray-600">Pending Requests</div>
          </Link>
          <Link to="/driver/calendar" data-tour="upcoming-rides" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-3xl font-bold text-green-500 mb-2">{upcomingRides.length}</div>
            <div className="text-gray-600">Upcoming Rides</div>
          </Link>
          <Link to="/driver/payouts" data-tour="payouts" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-gray-600">Payout History</div>
          </Link>
          <Link to="/driver/availability" data-tour="availability" className={`bg-white rounded-lg shadow p-6 hover:shadow-lg transition relative ${hasAvailabilityIssue ? 'border-2 border-red-400' : ''}`}>
            {hasAvailabilityIssue && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center" title="No availability set - riders cannot book you">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            )}
            <div className="text-sm text-gray-600">Manage</div>
            <div className={`text-lg font-semibold ${hasAvailabilityIssue ? 'text-red-600' : ''}`}>Availability</div>
            {hasAvailabilityIssue && (
              <div className="text-xs text-red-600 mt-1">Action needed</div>
            )}
          </Link>
          <Link to="/driver/pricing" data-tour="pricing" className={`bg-white rounded-lg shadow p-6 hover:shadow-lg transition relative ${hasPricingIssue ? 'border-2 border-red-400' : hasUnpricedSlots ? 'border-2 border-amber-400' : ''}`}>
            {(hasPricingIssue || hasUnpricedSlots) && (
              <div className={`absolute -top-2 -right-2 ${hasPricingIssue ? 'bg-red-500' : 'bg-amber-500'} text-white rounded-full w-6 h-6 flex items-center justify-center`} title={hasPricingIssue ? "No pricing set - riders cannot book you" : "Some availability slots need pricing"}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            )}
            <div className="text-sm text-gray-600">Set</div>
            <div className={`text-lg font-semibold ${hasPricingIssue ? 'text-red-600' : hasUnpricedSlots ? 'text-amber-600' : ''}`}>Pricing</div>
            {(hasPricingIssue || hasUnpricedSlots) && (
              <div className={`text-xs mt-1 ${hasPricingIssue ? 'text-red-600' : 'text-amber-600'}`}>Action needed</div>
            )}
          </Link>
          <Link to="/driver/day-rate-pricing" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition border-2 border-orange-200">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-sm text-gray-600">Configure</div>
            <div className="text-lg font-semibold text-orange-600">Day Rates</div>
          </Link>
        </div>
        {/* Today's Active Rides Section */}
        {todayActiveRides.length > 0 && (
          <div className="mb-6" data-tour="active-rides">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Today's Active Rides</h2>
              <span className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </span>
            </div>
            <div className="space-y-3">
              {todayActiveRides.map(ride => (
                <ActiveRideCard
                  key={ride.id}
                  ride={ride}
                  onComplete={handleCompleteUpcomingRide}
                  onDidNotOccur={handleDidNotOccur}
                  onCancel={() => fetchDashboardData(true)}
                  commissionRate={profile?.commission_rate || 0.30}
                />
              ))}
            </div>
          </div>
        )}

        {/* Recent Ride Requests */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-bold mb-4">Recent Ride Requests</h3>
          {rideRequests.length === 0 ? (
            <p className="text-gray-500">No pending ride requests</p>
          ) : (
            <div className="space-y-4">
              {rideRequests.slice(0, 3).map(request => (
                <div key={request.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      {request.rider_name && (
                        <div className="text-sm font-medium text-blue-600 mb-1">
                          👤 {request.rider_name}
                        </div>
                      )}
                      <div className="font-semibold">
                        {request.pickup_address || request.pickup_location} → {request.dropoff_address || request.dropoff_location}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {formatDateTime(request.requested_pickup_time, 'full')}
                      </div>
                      <div className="text-sm text-gray-600">
                        {request.distance_miles} miles • ${request.counter_offer_price || request.quoted_price}
                      </div>
                      {request.status === 'counter_offered' && (
                        <span className="inline-block mt-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                          Counter Offer Sent
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to="/driver/ride-requests"
                        className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {rideRequests.length > 3 && (
            <Link to="/driver/ride-requests" className="block mt-4 text-blue-500 hover:underline text-center">
              View all {rideRequests.length} requests
            </Link>
          )}
        </div>

        {/* Day Booking Requests */}
        {dayBookingRequests.length > 0 && (
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg shadow-lg border-2 border-orange-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-orange-800 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Day Booking Requests
              </h3>
              <span className="bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                {dayBookingRequests.length} Pending
              </span>
            </div>
            <div className="space-y-4">
              {dayBookingRequests.slice(0, 3).map(booking => (
                <div key={booking.id} className={`bg-white border-2 rounded-lg p-4 hover:shadow-md transition ${
                  booking.status === 'pending' && booking.counter_offer_flat_rate
                    ? 'border-green-400 bg-green-50'
                    : 'border-orange-200'
                }`}>
                  {/* Counter Offer Accepted Banner */}
                  {booking.status === 'pending' && booking.counter_offer_flat_rate && (
                    <div className="mb-3 px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium flex items-center gap-2">
                      <span>✓</span>
                      <span>Counter Offer Accepted! Rider agreed to ${parseFloat(booking.counter_offer_flat_rate).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-semibold text-orange-800">
                          {booking.rate_type === 'half_day' ? '🌅 Half-Day' : '☀️ Full-Day'}
                        </span>
                        <span className="text-sm text-gray-600">
                          👤 {booking.rider_name}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div>
                          📅 {new Date(ensureUTC(booking.start_time)).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                            
                          })} {new Date(ensureUTC(booking.start_time)).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            
                          })} CT
                        </div>
                        <div>
                          🚗 {booking.legs?.length || 0} trip{booking.legs?.length !== 1 ? 's' : ''} • 📏 {booking.total_mileage} miles
                        </div>
                        <div className="font-semibold text-green-600">
                          💰 Your Payout (net of tax): ${booking.driver_payout}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to="/driver/ride-requests"
                        className={`px-4 py-2 text-white text-sm rounded-lg font-medium transition ${
                          booking.status === 'pending' && booking.counter_offer_flat_rate
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-orange-600 hover:bg-orange-700'
                        }`}
                      >
                        {booking.status === 'pending' && booking.counter_offer_flat_rate
                          ? 'Confirm Booking'
                          : 'View Details'}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {dayBookingRequests.length > 3 && (
              <Link to="/driver/ride-requests" className="block mt-4 text-orange-600 hover:underline text-center font-semibold">
                View all {dayBookingRequests.length} day booking requests
              </Link>
            )}
          </div>
        )}

        {/* Scheduled Day Bookings - Show confirmed day bookings with cancel option */}
        {scheduledDayBookings.length > 0 && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg border-2 border-blue-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-blue-800 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Scheduled Day Bookings
              </h3>
              <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                {scheduledDayBookings.length} Confirmed
              </span>
            </div>
            <div className="space-y-4">
              {scheduledDayBookings.map(booking => (
                <div key={booking.id} className="bg-white border-2 border-blue-200 rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-semibold text-blue-800">
                          {booking.rate_type === 'half_day' ? '🌅 Half-Day' : '☀️ Full-Day'}
                        </span>
                        <span className="text-sm text-gray-600">
                          👤 {booking.rider_name}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          booking.status === 'in_progress'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {booking.status === 'in_progress' ? 'In Progress' : 'Scheduled'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div>
                          📅 {new Date(ensureUTC(booking.start_time)).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                            
                          })} at {new Date(ensureUTC(booking.start_time)).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            
                          })} CT
                        </div>
                        <div>
                          🚗 {booking.leg_count || 0} trip{booking.leg_count !== 1 ? 's' : ''} • 📏 {booking.total_mileage} miles
                        </div>
                        <div className="font-semibold text-green-600">
                          💰 Your Payout (net of tax): ${parseFloat(booking.driver_payout || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {booking.status === 'scheduled' && (
                        <button
                          onClick={() => setDayBookingCancelModal({ isOpen: true, booking })}
                          className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded border border-red-300 transition"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Week Schedule Summary */}
        <div className="mb-6" data-tour="week-calendar">
          <WeekScheduleSummary />
        </div>

        {/* Upcoming Rides */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">Upcoming Rides</h3>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : upcomingRides.length === 0 ? (
            <p className="text-gray-500">No upcoming rides scheduled</p>
          ) : (
            <div className="space-y-4">
              {upcomingRides.slice(0, 3).map(ride => (
                <UpcomingRideCard
                  key={ride.is_day_booking ? `day-${ride.id}` : `ride-${ride.id}`}
                  ride={ride}
                  onCancel={() => fetchDashboardData(true)}
                  onComplete={handleCompleteUpcomingRide}
                />
              ))}
            </div>
          )}
          {upcomingRides.length > 3 && (
            <Link to="/driver/calendar" className="block mt-4 text-blue-500 hover:underline text-center">
              View full calendar ({upcomingRides.length} total rides)
            </Link>
          )}
        </div>

        {/* Recent Rides */}
        <div className="bg-white rounded-lg shadow p-6 mt-6" data-tour="recent-rides">
          <h3 className="text-xl font-bold mb-4">Recent Rides</h3>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : recentRides.length === 0 ? (
            <p className="text-gray-500">No completed rides yet</p>
          ) : (
            <div className="space-y-4">
              {recentRides.map(ride => (
                <div key={ride.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {/* Day booking badge */}
                      {ride.is_day_booking && (
                        <span className="inline-block mb-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                          {ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} ({ride.leg_count} {ride.leg_count === 1 ? 'trip' : 'trips'})
                        </span>
                      )}
                      <div className="font-semibold">
                        {ride.pickup_address || ride.pickup_location} → {ride.dropoff_address || ride.dropoff_location}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {ride.pickup_datetime && new Date(ensureUTC(ride.pickup_datetime)).toLocaleString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </div>
                      <div className="text-sm text-gray-600">
                        {ride.rider_name} • {(ride.distance_miles || 0).toFixed(1)} miles
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">
                        ${(ride.driver_payout || 0).toFixed(2)}
                      </div>
                      {ride.tip_amount > 0 && (
                        <div className="text-xs text-gray-500">
                          (incl. ${(ride.tip_amount || 0).toFixed(2)} tip)
                        </div>
                      )}
                      <span className={`inline-block mt-1 px-2 py-1 text-xs rounded ${
                        ride.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {ride.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Link 
            to="/driver/ride-history" 
            className="block mt-4 text-blue-500 hover:underline text-center"
          >
            View Full History
          </Link>
        </div>
      </main>

      {/* Share Booking Link Modal */}
      <ShareBookingLinkModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />

      {/* Day Booking Cancel Modal for Drivers */}
      {dayBookingCancelModal.isOpen && dayBookingCancelModal.booking && (
        <DriverDayBookingCancelModal
          booking={dayBookingCancelModal.booking}
          onClose={() => setDayBookingCancelModal({ isOpen: false, booking: null })}
          onCancel={handleCancelDayBooking}
        />
      )}

      {/* Dashboard Tutorial */}
      {showTutorial && (
        <DashboardTutorial
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
          onOpenInviteFlow={() => {
            // Driver clicked "Invite Riders to Book" on the final walkthrough step
            setShowShareModal(true);
            handleInviteStepDismiss();
          }}
          onSkipInviteStep={() => {
            // Driver clicked "Skip for Now" on the final walkthrough step
            handleInviteStepDismiss();
          }}
        />
      )}

      {/* Standalone Invite Step Modal — shown when walkthrough was skipped */}
      {showInviteModal && !showTutorial && (
        <InviteStepModal
          isOpen={showInviteModal}
          onOpenInviteFlow={() => {
            setShowInviteModal(false);
            setShowShareModal(true);
            handleInviteStepDismiss();
          }}
          onDismiss={() => {
            setShowInviteModal(false);
            handleInviteStepDismiss();
          }}
        />
      )}

      {/* App Download Modal — shown after walkthrough + invite step complete */}
      <AppDownloadModal
        isOpen={showAppDownload}
        onClose={handleAppDownloadDismiss}
        title="Get Real-Time Ride Notifications"
        subtitle="Download the RideHowl app to get instant alerts for new ride requests, booking confirmations, and important account updates — so you never miss a fare."
        iosOnly={true}
        dismissLabel="Maybe Later"
      />
    </div>
  );
};

export default DriverDashboard;
